import {apiClient} from '../../../../../utils/api-client';
import {CategorySuggestion, NodeSuggestion} from '../interface';
import {useQueries} from '@tanstack/react-query';
import {InputSeparationVariant} from '../parse-input';
import {useDebouncedSeparationVariants} from './use-debounced-separation-variants';

const REQUEST_LIMIT = 30;

interface Props {
    inputSeparationVariants: InputSeparationVariant[];
    globalVersionId?: number;
}

interface NodeSuggestionDTO extends Omit<NodeSuggestion, 'type' | 'nodeType'> {
    type: string;
}

function isNodeSuggestionDTO(
    loadedSuggestion: NodeSuggestionDTO | CategorySuggestion
): loadedSuggestion is NodeSuggestionDTO {
    return (loadedSuggestion as NodeSuggestionDTO).nodeId !== undefined;
}

function nodeOrCategorySuggestionsRequest(
    input: string,
    prefix: string | null,
    globalVersionId?: number
) {
    return apiClient
        .get<Array<NodeSuggestionDTO | CategorySuggestion>>({
            path: 'autocomplete-service/api/autocomplete/suggestions',
            query: {
                input,
                type: prefix,
                'global-version': globalVersionId,
                limit: REQUEST_LIMIT
            }
        })
        .then((result) =>
            result.map((suggestion) =>
                isNodeSuggestionDTO(suggestion)
                    ? ({
                          ...suggestion,
                          type: 'node',
                          nodeType: suggestion.type
                      } as NodeSuggestion)
                    : suggestion
            )
        );
}

export const useNodeOrCategorySuggestions = (props: Props) => {
    const {debouncedSeparationVariants} = useDebouncedSeparationVariants(
        props.inputSeparationVariants
    );

    return useQueries({
        queries: debouncedSeparationVariants.map((variant) => ({
            queryKey: [
                'autocomplete-suggestions',
                variant.inputPrefix,
                variant.inputValue,
                props.globalVersionId
            ],
            queryFn: () =>
                nodeOrCategorySuggestionsRequest(
                    variant.inputValue,
                    variant.inputPrefix || null,
                    props.globalVersionId
                )
        })),
        combine: (result) => ({
            isLoading: result.length && result.every((query) => query.isLoading),
            data: result.map((query) => query.data || []).flat()
        })
    });
};
